/**
 * music-video-preview.js — see the edit BEFORE it is rendered.
 *
 * /music-video-maker asked people to commit 2–14 minutes of encoding (90 d
 * average: 164 s on the WebCodecs lane, 822 s on the FFmpeg one) to find out
 * what the montage looks like. The funnel says what that costs: 448 sessions
 * started a render and 281 downloaded anything — and the plan, which is the
 * thing that decides whether a montage reads as a montage, was invisible until
 * the file existed.
 *
 * This module draws the PLAN and plays it back:
 *
 *   · timeline — the song's waveform, the beat grid the cuts land on, and one
 *     coloured block per segment, so the rhythm and the clip order are legible
 *     at a glance and a bad plan is obvious before a single frame is encoded;
 *   · picture  — the actual source frame under the playhead, decoded by a
 *     pooled <video> per clip; the clip's poster is the fallback for footage
 *     the element cannot decode (the FFmpeg-only tier);
 *   · playback — the song plus the cuts, in real time, so «под бит» is
 *     something you HEAR rather than something you hope for.
 *
 * Nothing here touches the render path: the preview is a reader of the same
 * plan the encoder gets, so it cannot make the output differ from itself.
 *
 *   var pv = MusicVideoPreview.create({canvas, track, head, ...});
 *   pv.setSong(song); pv.setClips(clips); pv.setPlan(segs); pv.seek(0);
 */
(function () {
'use strict';
if (window.MusicVideoPreview) return;

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
function fmt(sec) {
    sec = Math.max(0, Math.round(sec || 0));
    return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0');
}
/* Distinct, stable hue per clip index. Colour is the only thing that says
   «this block and that block are the same footage», which is exactly the
   question someone judging a montage plan is asking. */
function clipHue(i) { return (i * 47) % 360; }
function clipColor(i, a) { return 'hsla(' + clipHue(i) + ',72%,58%,' + (a == null ? 1 : a) + ')'; }

function create(opts) {
    var canvas = opts.canvas, track = opts.track, head = opts.head;
    var playBtn = opts.playBtn, playIcon = opts.playIcon;
    var tcNow = opts.tcNow, tcAll = opts.tcAll, cutInfo = opts.cutInfo;
    var T = opts.t || {};
    var onSeek = opts.onSeek || function () {};

    var song = null, clips = [], segs = [], total = 0;
    var peaks = null, peaksFor = null;
    var playing = false, playT = 0, raf = 0, startedAt = 0, startOffset = 0;
    var audio = null, audioUrl = '';
    var pool = {};              // clipIndex -> { el, url, ok, posterImg }
    var curSeg = -1;
    var destroyed = false;
    var ctx2d = canvas ? canvas.getContext('2d', { alpha: false }) : null;
    var tctx = null;

    function t(k, vars) {
        var s = T[k] || '';
        if (vars) for (var n in vars) s = s.split(':' + n).join(vars[n]);
        return s;
    }

    /* ── Waveform peaks (min/max per column, straight off the decoded song) ── */
    function buildPeaks(cols) {
        if (!song || !song.buffer) return null;
        if (peaks && peaksFor === cols) return peaks;
        var buf = song.buffer, ch = buf.getChannelData(0);
        var n = ch.length, step = Math.max(1, Math.floor(n / cols));
        var out = new Float32Array(cols * 2);
        for (var c = 0; c < cols; c++) {
            var s = c * step, e = Math.min(n, s + step), mn = 0, mx = 0;
            // stride the window: a 10-minute song is 26 M samples and the
            // picture is 1200 px wide — reading every sample buys nothing
            var hop = Math.max(1, Math.floor((e - s) / 512));
            for (var i = s; i < e; i += hop) { var v = ch[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
            out[c * 2] = mn; out[c * 2 + 1] = mx;
        }
        peaks = out; peaksFor = cols;
        return out;
    }

    /* ── Timeline: waveform + beat ticks + segment blocks ── */
    function drawTrack() {
        if (!track || destroyed) return;
        var cv = track.querySelector('canvas'); if (!cv) return;
        if (!tctx) tctx = cv.getContext('2d');
        var dpr = Math.min(2, window.devicePixelRatio || 1);
        var w = Math.max(120, Math.round(track.clientWidth * dpr));
        var h = Math.max(40, Math.round(56 * dpr));
        if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; peaksFor = null; }
        var cs = getComputedStyle(document.documentElement);
        var muted = (cs.getPropertyValue('--text-muted') || '#94a3b8').trim();
        tctx.clearRect(0, 0, w, h);

        var dur = total || (song ? song.duration : 0);
        if (!(dur > 0)) return;

        // segment blocks — the band that says WHICH clip is on screen when
        var bandH = Math.round(h * 0.3), bandY = h - bandH;
        var acc = 0;
        for (var s = 0; s < segs.length; s++) {
            var x0 = (acc / dur) * w, x1 = ((acc + segs[s].dur) / dur) * w;
            tctx.fillStyle = clipColor(segs[s].clipIndex, 0.85);
            tctx.fillRect(x0, bandY, Math.max(1, x1 - x0 - 1), bandH);
            acc += segs[s].dur;
        }

        // waveform of the song, over the part of the timeline it actually covers
        var cols = Math.round(w / dpr / 2);
        var pk = buildPeaks(cols);
        if (pk && song) {
            var songW = w * Math.min(1, song.duration / dur);
            var mid = (h - bandH) / 2, amp = (h - bandH) / 2 - 2;
            tctx.fillStyle = 'rgba(56,189,248,.55)';
            for (var c = 0; c < cols; c++) {
                var x = (c / cols) * songW;
                var y0 = mid - pk[c * 2 + 1] * amp, y1 = mid - pk[c * 2] * amp;
                tctx.fillRect(x, y0, Math.max(1, songW / cols - 0.5), Math.max(1, y1 - y0));
            }
            // the song ENDS here and the montage keeps going — say so in the
            // picture rather than only in a sentence under the button
            if (song.duration < dur - 0.2) {
                var sx = songW;
                tctx.fillStyle = 'rgba(148,163,184,.16)';
                tctx.fillRect(sx, 0, w - sx, h - bandH);
                tctx.strokeStyle = muted; tctx.lineWidth = dpr;
                tctx.beginPath(); tctx.moveTo(sx, 0); tctx.lineTo(sx, h - bandH); tctx.stroke();
            }
        }

        // cut marks on top of everything
        tctx.strokeStyle = 'rgba(255,255,255,.55)'; tctx.lineWidth = Math.max(1, dpr * 0.75);
        acc = 0;
        for (var q = 0; q < segs.length; q++) {
            acc += segs[q].dur;
            if (acc >= dur - 0.01) break;
            var cx = (acc / dur) * w;
            tctx.beginPath(); tctx.moveTo(cx, 0); tctx.lineTo(cx, h - bandH); tctx.stroke();
        }
    }

    /* ── Picture ── */
    function segAt(tSec) {
        var acc = 0;
        for (var i = 0; i < segs.length; i++) {
            if (tSec < acc + segs[i].dur - 1e-6) return { i: i, into: tSec - acc };
            acc += segs[i].dur;
        }
        return segs.length ? { i: segs.length - 1, into: segs[segs.length - 1].dur } : null;
    }

    function poolFor(ci) {
        if (pool[ci]) return pool[ci];
        var clip = clips[ci];
        var rec = { el: null, url: '', ok: false, poster: null };
        if (!clip) return (pool[ci] = rec);
        /* A source becoming READY has to repaint the frame that is already on
           screen. Without it the first paint happens while the <video> is still
           loading and the poster (when there is one at all — the FFmpeg-probe
           tier has none) has not decoded, so the screen stays black until the
           first play. Measured on synthetic clips with no poster: 0 non-black
           pixels after 2.5 s on a plan that was fully drawn. */
        var wake = function () { if (!playing) drawAt(playT, true); };
        if (clip.thumbUrl) {
            var im = new Image(); im.src = clip.thumbUrl; rec.poster = im;
            im.addEventListener('load', wake, { once: true });
        }
        // ffonly clips are exactly the ones <video> could not decode during the
        // probe ladder — asking it again would only produce another error.
        // A still is not a video at all: its poster IS the frame.
        if (!clip.ffonly && !clip.isImage) {
            try {
                var v = document.createElement('video');
                v.muted = true; v.playsInline = true; v.preload = 'auto';
                v.setAttribute('playsinline', ''); v.setAttribute('muted', '');
                rec.url = URL.createObjectURL(clip.file);
                v.src = rec.url;
                v.addEventListener('loadeddata', function () { rec.ok = true; wake(); }, { once: true });
                // A <video> only decodes a frame once it has been asked for a
                // time; without this the element sits at readyState 1 forever
                // on a page that never presses play.
                v.addEventListener('loadedmetadata', function () {
                    try { v.currentTime = Math.min(0.04, (v.duration || 1) / 2); } catch (_) {}
                }, { once: true });
                v.addEventListener('seeked', wake);
                v.addEventListener('error', function () { rec.ok = false; }, { once: true });
                rec.el = v;
            } catch (_) {}
        }
        pool[ci] = rec;
        return rec;
    }

    function paint(src, W, H) {
        if (!ctx2d) return;
        ctx2d.fillStyle = '#05070d';
        ctx2d.fillRect(0, 0, canvas.width, canvas.height);
        if (!src) return;
        var sw = W || src.videoWidth || src.naturalWidth || 0;
        var sh = H || src.videoHeight || src.naturalHeight || 0;
        if (!sw || !sh) return;
        var cw = canvas.width, chh = canvas.height;
        var r = Math.min(cw / sw, chh / sh);          // contain — never crop the preview
        var dw = sw * r, dh = sh * r;
        try { ctx2d.drawImage(src, (cw - dw) / 2, (chh - dh) / 2, dw, dh); } catch (_) {}
    }

    function drawAt(tSec, seeking) {
        if (destroyed || !segs.length) return;
        var at = segAt(tSec); if (!at) return;
        var seg = segs[at.i];
        var ci = (seg.cells && seg.cells.length ? seg.cells[0].clipIndex : seg.clipIndex);
        var rec = poolFor(ci);
        var srcT = (seg.cells && seg.cells.length ? seg.cells[0].start : seg.start) + at.into;
        var clip = clips[ci];
        if (clip && srcT > clip.duration - 0.05) srcT = Math.max(0, clip.duration - 0.05);

        if (cutInfo) {
            cutInfo.textContent = t('cut_info', { n: at.i + 1, total: segs.length });
            cutInfo.classList.remove('mvm-hidden');
        }

        if (rec.el && rec.ok) {
            if (seeking || !playing) {
                try {
                    rec.el.currentTime = srcT;
                    // one-shot: the frame is only ready after the seek lands
                    var once = function () { paint(rec.el); rec.el.removeEventListener('seeked', once); };
                    rec.el.addEventListener('seeked', once);
                } catch (_) { paint(rec.poster); }
            }
            if (rec.el.readyState >= 2) paint(rec.el);
            else if (rec.poster && rec.poster.complete) paint(rec.poster);
        } else if (rec.poster && rec.poster.complete) {
            paint(rec.poster);
        } else if (rec.poster) {
            rec.poster.addEventListener('load', function () { paint(rec.poster); }, { once: true });
        }
    }

    /* ── Transport ── */
    function setPlayIcon(on) {
        if (playIcon) playIcon.innerHTML = on
            ? '<path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z"/>'
            : '<path d="M6 4l14 8-14 8V4z"/>';
        if (playBtn) {
            var lbl = on ? t('preview_pause') : t('preview_play');
            playBtn.setAttribute('aria-label', lbl); playBtn.title = lbl;
        }
    }

    function tick() {
        if (!playing || destroyed) return;
        var now = (audio && !audio.paused) ? audio.currentTime : ((performance.now() - startedAt) / 1000 + startOffset);
        playT = now;
        if (playT >= total - 0.02) { pause(); seek(0); return; }
        var at = segAt(playT);
        if (at && at.i !== curSeg) {
            curSeg = at.i;
            var seg = segs[at.i];
            var ci = (seg.cells && seg.cells.length ? seg.cells[0].clipIndex : seg.clipIndex);
            var rec = poolFor(ci);
            if (rec.el && rec.ok) {
                var srcT = (seg.cells && seg.cells.length ? seg.cells[0].start : seg.start);
                try { rec.el.currentTime = srcT; rec.el.play().catch(function () {}); } catch (_) {}
            }
        }
        // draw whatever the active element is showing right now
        if (at) {
            var sg = segs[at.i];
            var ci2 = (sg.cells && sg.cells.length ? sg.cells[0].clipIndex : sg.clipIndex);
            var r2 = pool[ci2];
            if (r2 && r2.el && r2.ok && r2.el.readyState >= 2) paint(r2.el);
            else if (r2 && r2.poster && r2.poster.complete) paint(r2.poster);
        }
        paintHead();
        raf = requestAnimationFrame(tick);
    }

    function paintHead() {
        if (head && total > 0) {
            var pct = clamp(playT / total, 0, 1) * 100;
            head.style.insetInlineStart = pct + '%';
        }
        if (tcNow) tcNow.textContent = fmt(playT);
        if (track) track.setAttribute('aria-valuenow', String(Math.round(playT)));
    }

    function play() {
        if (playing || !segs.length || destroyed) return;
        playing = true; setPlayIcon(true);
        startedAt = performance.now(); startOffset = playT;
        if (song && song.file) {
            if (!audio) {
                audio = document.createElement('audio');
                audioUrl = URL.createObjectURL(song.file);
                audio.src = audioUrl; audio.preload = 'auto';
            }
            try {
                audio.currentTime = Math.min(playT, Math.max(0, (song.duration || 0) - 0.05));
                /* Called straight out of the click handler, so the activation is
                   still alive — a play() after an await is refused on Safari and
                   Chrome-on-Android, and desktop Chromium's sticky activation
                   hides that locally. */
                var pr = audio.play();
                if (pr && pr.catch) pr.catch(function () {});
            } catch (_) {}
        }
        // stop the song when the montage outlasts it: what the render does too
        if (audio) audio.onended = function () { audio.pause(); };
        curSeg = -1;
        raf = requestAnimationFrame(tick);
    }

    function pause() {
        if (!playing) return;
        playing = false; setPlayIcon(false);
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        if (audio) try { audio.pause(); } catch (_) {}
        for (var k in pool) if (pool[k] && pool[k].el) try { pool[k].el.pause(); } catch (_) {}
        drawAt(playT, true);
    }

    function seek(tSec) {
        playT = clamp(tSec, 0, Math.max(0, total - 0.02));
        if (audio) try { audio.currentTime = Math.min(playT, Math.max(0, (song ? song.duration : 0) - 0.05)); } catch (_) {}
        curSeg = -1;
        paintHead();
        drawAt(playT, true);
    }

    /* ── Wiring ── */
    if (playBtn) playBtn.addEventListener('click', function () {
        if (playing) pause(); else play();
    });
    if (track) {
        var grab = function (e) {
            var r = track.getBoundingClientRect();
            var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
            var frac = clamp(x / Math.max(1, r.width), 0, 1);
            // RTL: the timeline reads right-to-left with the document
            if (getComputedStyle(track).direction === 'rtl') frac = 1 - frac;
            seek(frac * total); onSeek(playT);
        };
        var dragging = false;
        track.addEventListener('pointerdown', function (e) {
            dragging = true; try { track.setPointerCapture(e.pointerId); } catch (_) {}
            if (playing) pause();
            grab(e);
        });
        track.addEventListener('pointermove', function (e) { if (dragging) grab(e); });
        track.addEventListener('pointerup', function () { dragging = false; });
        track.addEventListener('pointercancel', function () { dragging = false; });
        track.addEventListener('keydown', function (e) {
            var step = e.shiftKey ? 10 : 1;
            if (e.key === 'ArrowRight') { seek(playT + step); e.preventDefault(); }
            else if (e.key === 'ArrowLeft') { seek(playT - step); e.preventDefault(); }
            else if (e.key === 'Home') { seek(0); e.preventDefault(); }
            else if (e.key === 'End') { seek(total); e.preventDefault(); }
            else if (e.key === ' ' || e.key === 'Enter') { playing ? pause() : play(); e.preventDefault(); }
        });
    }

    var ro = null;
    if (window.ResizeObserver && track) {
        ro = new ResizeObserver(function () { peaksFor = null; drawTrack(); });
        try { ro.observe(track); } catch (_) {}
    }
    // A hidden tab must not keep decoding video for a picture nobody is looking at.
    document.addEventListener('visibilitychange', function () { if (document.hidden && playing) pause(); });

    return {
        setSong: function (s) { song = s; peaks = null; peaksFor = null; if (audio) { try { audio.pause(); } catch (_) {} try { URL.revokeObjectURL(audioUrl); } catch (_) {} audio = null; } drawTrack(); },
        setClips: function (c) {
            // drop pooled elements for clips that are gone or replaced
            for (var k in pool) {
                var idx = +k;
                if (!c[idx] || (clips[idx] && c[idx] !== clips[idx])) {
                    if (pool[k].el) { try { pool[k].el.pause(); pool[k].el.removeAttribute('src'); pool[k].el.load(); } catch (_) {} }
                    if (pool[k].url) try { URL.revokeObjectURL(pool[k].url); } catch (_) {}
                    delete pool[k];
                }
            }
            clips = c || [];
        },
        setPlan: function (s) {
            segs = s || []; total = 0;
            for (var i = 0; i < segs.length; i++) total += segs[i].dur;
            if (tcAll) tcAll.textContent = fmt(total);
            if (track) track.setAttribute('aria-valuemax', String(Math.round(total)));
            if (playBtn) playBtn.disabled = !segs.length;
            if (playT > total) playT = 0;
            drawTrack(); paintHead();
            if (!playing) drawAt(playT, true);
        },
        setAspect: function (w, h) {
            if (!canvas || !w || !h) return;
            if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
            if (!playing) drawAt(playT, true);
        },
        redraw: function () { peaksFor = null; drawTrack(); if (!playing) drawAt(playT, true); },
        /* The montage is about to open decoders of its own: every pooled
           <video> lets go of its source (on Android each one can hold a
           hardware decoder). The pool refills itself on the next drawAt. */
        releaseMedia: function () {
            pause();
            for (var k in pool) {
                if (pool[k].el) { try { pool[k].el.pause(); pool[k].el.removeAttribute('src'); pool[k].el.load(); } catch (_) {} }
                if (pool[k].url) try { URL.revokeObjectURL(pool[k].url); } catch (_) {}
            }
            pool = {};
            if (audio) { try { audio.pause(); audio.removeAttribute('src'); audio.load(); } catch (_) {} audio = null; }
            if (audioUrl) { try { URL.revokeObjectURL(audioUrl); } catch (_) {} audioUrl = ''; }
        },
        pause: pause,
        isPlaying: function () { return playing; },
        time: function () { return playT; },
        destroy: function () {
            destroyed = true; pause();
            if (ro) try { ro.disconnect(); } catch (_) {}
            for (var k in pool) {
                if (pool[k].el) { try { pool[k].el.removeAttribute('src'); pool[k].el.load(); } catch (_) {} }
                if (pool[k].url) try { URL.revokeObjectURL(pool[k].url); } catch (_) {}
            }
            pool = {};
            if (audioUrl) try { URL.revokeObjectURL(audioUrl); } catch (_) {}
            audio = null;
        }
    };
}

window.MusicVideoPreview = { create: create, clipColor: clipColor };
})();
